<template>
   <div
      ref="root"
      class="fixed inset-0 z-[999]"
      :class="leaving ? 'alert-leaving pointer-events-none' : ''"
   >
      <div class="alert-overlay bg-elevated/75 absolute inset-0" @click="onOverlayClick" />
      <div class="flex h-full items-start justify-center px-4 pt-4">
         <div
            ref="target"
            class="alert-content __shadow rounded-base relative flex max-w-[450px] flex-col gap-1 bg-white p-4"
         >
            <div class="flex items-center gap-2">
               <UIcon
                  v-if="type === 'confirm'"
                  name="i-heroicons-information-circle"
                  class="text-primary size-6"
               />
               <UIcon
                  v-if="type === 'loading'"
                  name="i-heroicons-arrow-path"
                  class="text-primary size-6 animate-spin"
               />

               <p class="h-m">
                  {{ title }}
               </p>
            </div>
            <p v-if="description" class="p-m">
               {{ description }}
            </p>

            <div v-if="type === 'confirm'" class="mt-2 flex justify-end gap-2">
               <UButton variant="outline" @click="dismiss('cancel')">
                  {{ discardLabel }}
               </UButton>
               <UButton @click="dismiss('confirm')">
                  {{ confirmLabel }}
               </UButton>
            </div>
         </div>
      </div>
   </div>
</template>

<script lang="ts" setup>
import { ref } from 'vue'

type Props = {
   title: string
   description?: string
   type?: 'confirm' | 'loading'
   confirmLabel?: string
   discardLabel?: string
}

type Emits = {
   (e: 'confirm'): void
   (e: 'cancel'): void
   (e: 'after-leave'): void
}

const props = withDefaults(defineProps<Props>(), {
   type: 'confirm',
   confirmLabel: '',
   discardLabel: '',
})
const emit = defineEmits<Emits>()
const target = ref<HTMLElement>()
const root = ref<HTMLElement>()
const leaving = ref(false)
let pendingEvent: 'confirm' | 'cancel' | null = null

function onOverlayClick(e: MouseEvent) {
   if (props.type === 'confirm' && e.target === e.currentTarget) {
      dismiss('cancel')
   }
}

function leave() {
   if (leaving.value) return
   leaving.value = true
   setTimeout(() => {
      if (pendingEvent) emit(pendingEvent)
      emit('after-leave')
   }, 200)
}

defineExpose({
   dismiss() {
      leave()
   },
})

function dismiss(event: 'confirm' | 'cancel') {
   pendingEvent = event
   leave()
}
</script>

<style scoped>
.alert-overlay {
   animation: fade-in 200ms ease-out both;
}

.alert-content {
   animation: scale-in 200ms ease-out both;
}

.alert-leaving .alert-overlay {
   animation: fade-out 200ms ease-in both;
}

.alert-leaving .alert-content {
   animation: scale-out 200ms ease-in both;
}

@keyframes fade-in {
   from {
      opacity: 0;
   }
   to {
      opacity: 1;
   }
}

@keyframes fade-out {
   from {
      opacity: 1;
   }
   to {
      opacity: 0;
   }
}

@keyframes scale-in {
   from {
      opacity: 0;
      transform: scale(0.95);
   }
   to {
      opacity: 1;
      transform: scale(1);
   }
}

@keyframes scale-out {
   from {
      opacity: 1;
      transform: scale(1);
   }
   to {
      opacity: 0;
      transform: scale(0.95);
   }
}
</style>
